import React from "react";
import { Box, Button, Text, InlineNotification } from "@familyzone/component-library";
import { ImportScheduleFileList } from "./ImportScheduleFileList";
import { ScheduleCSVObject } from "./ScheduleCSVObject";

interface Props {
  scheduleObjects: Set<ScheduleCSVObject>;
  updateCsvObjectsCallback: (csvObjects: Set<ScheduleCSVObject>) => void;
  onParseError?: (csvObject: ScheduleCSVObject) => void;
}

export function NewScheduleImports(props: Props): JSX.Element {
  const handleScheduleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);

      // Create new ScheduleCSVObjects for the uploaded files
      const newScheduleObjects = new Set(props.scheduleObjects);
      for (const file of fileArray) {
        // Check if we already have an object for this file
        const existingFile = Array.from(newScheduleObjects).find((obj) => obj.getFile() === file);
        if (!existingFile) {
          const newObj = new ScheduleCSVObject(file);
          newScheduleObjects.add(newObj);
        }
      }
      props.updateCsvObjectsCallback(newScheduleObjects);

      // Reset the file input to allow selecting the same file again
      e.target.value = "";
    }
  };

  const handleUpdateScheduleCsvObjects = (csvObjects: Set<ScheduleCSVObject>) => {
    props.updateCsvObjectsCallback(csvObjects);
  };

  const handleDeleteScheduleCsvObject = (csvObjectToDelete: ScheduleCSVObject) => {
    const updatedObjects = new Set(Array.from(props.scheduleObjects).filter((csvObject) => csvObject !== csvObjectToDelete));
    props.updateCsvObjectsCallback(updatedObjects);
  };

  return (
    <Box>
      <input
        type="file"
        id="schedule_csv_files_input"
        data-testid="schedule_csv_files_input"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onChange={handleScheduleFileSelect}
        multiple
      />
      <Button variant="primary" data-testid="upload-schedules-button">
        <label className="importLabel" htmlFor="schedule_csv_files_input">
          Upload Classroom Schedules
        </label>
      </Button>
      <Text my={10}>CSV-uploaded schedules are inactive by default. Teachers must log in to Classroom Manager to activate them.</Text>
      <InlineNotification
        status="info"
        notificationTitle={"Expected Classroom Schedules CSV Format"}
        notificationDescription={
          <Box mt={5}>
            <Text my={10} fontWeight="500">
              Columns:
            </Text>
            <Text>Classroom name, Teacher username, Weekday, Start time, End time, End date</Text>
            <Text fontStyle={"italic"} mt={5}>
              All fields are required. Please do not include column titles in your CSV file.
            </Text>
            <Text my={10} fontWeight="500">
              Valid weekday formats:
            </Text>
            <Text>
              <code>MONDAY</code>, <code>TUESDAY</code>, <code>WEDNESDAY</code>, <code>THURSDAY</code>, <code>FRIDAY</code>,{" "}
              <code>SATURDAY</code>, <code>SUNDAY</code>.
            </Text>
            <Text my={10} fontWeight="500">
              Valid time format:
            </Text>
            <Text>
              24 hour format (<code>00:00-23:59</code>)
            </Text>
            <Text mt={5}>
              Eg. 9:00AM is <code>09:00</code>, 10:30AM is <code>10:30</code>, 3:00PM is <code>15:00</code>, etc.
            </Text>
            <Text my={10} fontWeight="500">
              Valid date format:
            </Text>
            <Text>
              <code>DD/MM/YYYY</code>
            </Text>
            <Text mt={5}>
              Eg. January 30th, 2025 is <code>30/01/2025</code>
            </Text>
            <Text my={10} fontWeight="500">
              Example:
            </Text>
            <Text>
              <code>classroom1, teacher1, MONDAY, 08:15, 09:45, 30/01/2025</code>
            </Text>
            <Text>
              <code>classroom1, teacher1, WEDNESDAY, 11:20, 11:59, 24/12/2025</code>
            </Text>
          </Box>
        }
      />
      <ImportScheduleFileList
        csvObjects={props.scheduleObjects}
        updateCsvObjectsCallback={handleUpdateScheduleCsvObjects}
        deleteCsvObjectCallback={handleDeleteScheduleCsvObject}
        onParseError={props.onParseError}
      />
    </Box>
  );
}
